<template>
  <div>
    <div
      v-if="loading"
      class="d-flex justify-center align-center"
      style="height: 100vh;"
    >
      <v-progress-circular
        indeterminate
        color="#2661FF"
        size="80"
      ></v-progress-circular>
    </div>
    <div v-else>
      <v-row class="px-2">
        <v-col cols="12" md="6">
          <h2
            class="text-h6 grey--text text--darken-3 font-weight-medium mb-2 ml-6 text-color"
            style="font-size: 20px !important;"
          >
            {{ $t('pricing_model.promotional_codes.title') }}
          </h2>
        </v-col>
        <!------------------------------------------>
        <!-- CREATE PROMO CODE DIALOG -------------->
        <!------------------------------------------>
        <v-col cols="12" md="6" class="d-flex justify-end align-center">
          <v-btn
            color="#2661FF"
            class="rounded-lg mb-2"
            @click="openCreatePromotionalCodeDialog"
          >
            <v-icon color="white">
              add
            </v-icon>
            <span style="color: white;">
              {{ $t('pricing_model.promotional_codes.create.title') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
      <!------------------------------------------>
      <!-- VIEW PROMO CODES ---------------------->
      <!------------------------------------------>
      <v-row>
        <v-col cols="12">
          <v-card class="rounded-lg" flat>
            <v-card-text class="px-10 pt-8">
              <v-row>
                <v-col cols="6" class="d-flex justify-start align-center">
                  <p class="grey--text text--darken-2 pt-2">
                    {{ $t('pricing_model.promotional_codes.description') }}
                  </p>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-text>
              <v-row no-gutters>
                <v-col cols="3">
                  <v-text-field
                    v-model="table.filters.search"
                    outlined
                    class="rounded-lg font-weight-regular ml-5"
                    :label="$t('actions.search')"
                    prepend-inner-icon="search"
                    clearable
                    @keyup.enter="fetchPromotionalCodes"
                    @click:clear="clearSearch"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-data-table
                    :loading="table.loading"
                    :headers="table.headers"
                    :items="table.items"
                    :server-items-length="table.total"
                    :items-per-page="table.pagination.itemsPerPage"
                    :footer-props="{
                      'items-per-page-options': [5, 10, 15, 20]
                    }"
                    :no-data-text="$t('subscriptions_report.no_data')"
                    :no-results-text="$t('subscriptions_report.no_data')"
                    @update:options="$emit('update:table', $event)"
                  >
                    <!-- SHOWS NO DATA -->
                    <template v-slot:no-data>
                      <div class="text-center pa-5 my-5">
                        <v-img
                          :src="require('@/assets/svg/box_empty.svg')"
                          height="150"
                          max-height="150px"
                          aspect-ratio="1"
                          contain
                        />
                        <div class="grey--text">
                          {{ $t('subscriptions_report.no_data') }}
                        </div>
                      </div>
                    </template>

                    <template v-slot:item.id="{ item }">
                      {{ item.id }}
                    </template>
                    <template v-slot:item.name="{ item }">
                      {{ item.name }}
                    </template>
                    <template v-slot:item.code="{ item }">
                      {{ item.code }}
                    </template>
                    <template v-slot:item.expiration_date="{ item }">
                      {{ item.expiration_date | moment('lll') }}
                    </template>
                    <template v-slot:item.status="{ item }">
                      <v-chip :color="getItemStatus(item.status).color">
                        {{ getItemStatus(item.status).text }}
                      </v-chip>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <div class="d-flex justify-center align-center">
                        <v-btn
                          icon
                          @click="openEditPromotionalCodeDialog(item)"
                        >
                          <v-icon>
                            edit_square
                          </v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          @click="openDeletePromotionalCodeDialog(item.id)"
                          :loading="deleteDialog.loading"
                        >
                          <v-icon>
                            delete
                          </v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <!------------------------------------------>
    <!-- CREATE OR EDIT PROMO CODE ------------->
    <!------------------------------------------>
    <HandlePromotionalCodeDialog
      v-if="handlePromotionalCodeDialog.display"
      :value="handlePromotionalCodeDialog"
      @close="handlePromotionalCodeDialog.display = false"
      @update:display="handlePromotionalCodeDialog.display = $event"
      @fetchPromotionalCodes="$emit('fetchPromotionalCodes')"
    />
    <!------------------------------------------>
    <!-- DELETE PROMO CODE --------------------->
    <!------------------------------------------>
    <BaseConfirmDialog
      v-if="deleteDialog.display"
      :cancel-text="$t('general.cancel')"
      :accept-text="$t('general.delete')"
      @close="deleteDialog = false"
      @proceed="confirmRemove()"
      :title="$t('pricing_model.promotional_codes.delete.title')"
      :description="
        $t('pricing_model.promotional_codes.delete.description', {
          code: deleteDialog.selectedId
        })
      "
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import HandlePromotionalCodeDialog from '@/components/Addon/PromotionalCodes/HandlePromotionalCodeDialog.vue'
import BaseConfirmDialog from '@/components/BaseConfirmDialog.vue'

export default {
  name: 'ManagePromotionalCodes',
  components: { HandlePromotionalCodeDialog, BaseConfirmDialog },
  props: {
    table: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      handlePromotionalCodeDialog: {
        item: {},
        type: 'create',
        display: false,
        pricing_model_id: null
      },
      deleteDialog: {
        display: false,
        loading: false,
        selectedId: null
      }
    }
  },
  // watch: {
  //   'promoCodesTable.pagination': {
  //     async handler() {
  //       await this.fetchPromotionalCodes()
  //     },
  //     deep: true
  //   }
  // },
  computed: {
    ...mapState({
      pricingModel: ({ pricingModels: { pricingModel } }) => pricingModel
    })
  },
  created() {
    this.handlePromotionalCodeDialog.pricing_model_id = this.pricingModel.id
  },
  methods: {
    ...mapActions({
      removePromotionalCode: 'pricingModels/removePromotionalCode'
    }),
    // async fetchPromotionalCodes() {
    //   const { promoCodesTable } = this
    //   const { search } = promoCodesTable.filters
    //   const { page, itemsPerPage } = promoCodesTable.pagination
    //   let { sortBy, sortDesc = false } = promoCodesTable.pagination

    //   promoCodesTable.loading = true

    //   if (Array.isArray(sortBy)) {
    //     ;[sortBy] = sortBy
    //   }
    //   if (Array.isArray(sortDesc)) {
    //     ;[sortDesc] = sortDesc
    //   }

    //   const payload = {
    //     page,
    //     sortBy,
    //     search,
    //     sortDesc,
    //     itemsPerPage
    //   }

    //   await this.getPromotionalCodes(payload)

    //   this.promoCodesTable.items = this.promotionalCodes
    //   this.promoCodesTable.total = parseInt(this.promotionalCodesTotal, 10)

    //   promoCodesTable.loading = false
    // },
    getItemStatus(status) {
      const [ACTIVE, DISABLED] = [1, 2]

      const mappedStatuses = {
        [ACTIVE]: {
          text: this.$t('pricing_model.promotional_codes.statuses.active'),
          color: 'success'
        },
        [DISABLED]: {
          text: this.$t('pricing_model.promotional_codes.statuses.disabled'),
          color: 'error'
        }
      }

      return (
        {
          text: mappedStatuses[status].text,
          color: mappedStatuses[status].color
        } || {
          text: this.$t('pricing_model.promotional_codes.statuses.unknown'),
          color: 'grey'
        }
      )
    },
    async clearSearch() {
      this.table.filters.search = null
      this.$emit('fetchPromotionalCodes')
    },
    async openCreatePromotionalCodeDialog() {
      this.handlePromotionalCodeDialog.display = true
      this.handlePromotionalCodeDialog.type = 'create'
    },
    async openDeletePromotionalCodeDialog(id) {
      this.deleteDialog.selectedId = id
      this.deleteDialog.display = true
    },
    async confirmRemove() {
      const { deleteDialog } = this
      deleteDialog.loading = true
      await this.removePromotionalCode(deleteDialog.selectedId)
      deleteDialog.loading = false
      this.deleteDialog.display = false
      await this.$emit('fetchPromotionalCodes')
    },
    openEditPromotionalCodeDialog(item) {
      this.handlePromotionalCodeDialog.display = true
      this.handlePromotionalCodeDialog.type = 'edit'
      this.handlePromotionalCodeDialog.item = item
    }
  }
}
</script>

<style lang="scss">
.text-color {
  color: #344054 !important;
}

.header-color {
  color: #2661ff !important;
}
</style>
